/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

/**
 * PrivateRoute can be used in place of a react-router Route component.
 * It ensures that a user is authenticated before allowing them to view a route.
 * If the user is not authenticated, they will be directed to the login page.
 */

import { Loading } from 'carbon-components-react';
import React from 'react';
import { Route, Navigate, useLocation } from 'react-router-dom';

import { useAppSelector } from '../../../../store';
import {
  isAuthenticated as selectIsAuthenticated,
  isChecking as selectIsChecking,
  isInitialized as selectIsInitialized
} from '../../store/session/selectors';

interface PrivateRouteProps {
  component?: React.ComponentType<any>;
  children?:
    | any
    | React.ReactChildren
    | ((props: typeof Route) => React.ReactNode);
  path?: string;
  render?: (props: typeof Route) => React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = props => {
  const { component: OriginalComponent, children, ...rest } = props;
  const { pathname } = useLocation();

  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isChecking = useAppSelector(selectIsChecking);
  const isInitialized = useAppSelector(selectIsInitialized);

  if (!isInitialized || isChecking) {
    return (
      <div>
        <Loading />
      </div>
    );
  }
  const navState = { from: pathname };

  if (!isAuthenticated) {
    return <Navigate to="/login" state={navState} replace />;
  }
  if (OriginalComponent) {
    return <OriginalComponent {...rest} />;
  }

  return children;
};

export default PrivateRoute;
